import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "../../../Routes/paths";

import { ChartLegend, PopoverCard, Switcher } from "../../../Components/Atoms";
import { ModelPicker, ReactTable } from "../../../Components/Molecules";
import { Stack } from "../../../Components/Organisms/Graphs";
import { Report } from "../../../Templates";

import { getStatsData, getVehicleStats } from "../../../APIs";
import { StoreContext } from "../../../Library/contexts";
import { DATE_RANGES } from "../../../Library/constants";
import { MODULE_TYPES, STATS_ENUMS } from "../../../Library/widgets";

import { Checkbox, IconButton } from "@chakra-ui/react";
import { DownloadIcon } from "../../../Assets/images/svgs";
import { THRESHOLD_TABLE_HEADERS } from "../../../Library/tableHeaders";
import { getDateTimeRows, getUserAndVehicle } from "../../../Library/helpers";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";

const labelData = [ "1", "2", "3", "4", "5+" ];
const parser = { x: "model", y: "count", xLabel: "Vehicles Count", yLabel: "Times of Threshold Crossed" };
const paths = [
  { idx: 0, name: "Analytics", link: PATHS.ANALYTICS },
  { idx: 1, name: "Battery Temperature", isCurrentPage: true },
];

const BatteryTemperature = () => {
  const navigate = useNavigate();
  const { filters } = useContext(StoreContext);
  const [ showChargingData, setShowChargingData ] = useState(true);
  const handleChange = (e) => setShowChargingData(e.target.checked);

  const reportRef = useRef(null);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ selected, setSelected ] = useState();
  const [ categories, setCategories ] = useState();
  const [ pageData, setPageData ] = useState();
  const [ batteryTempData, setBatteryTempData ] = useState([]);
  const [ showOverlay, setShowOverlay ] = useState(false);

  const processData = useCallback((data, constants) => {
    // For Grouping Counts >= 5
    const processed = [];
    const legends = [];

    data.forEach((d) => {
      let counts = [];

      d.count.forEach((c) => {
        let index = (c.idx >= 5) ? 4 : c.idx - 1;

        if (counts[index]) counts[index] += c.count;
        else counts[index] = c.count;
      });

      processed.push({
        color: constants.modelMap[d.model].color,
        model: constants.modelMap[d.model].nm,
        count: counts,
        modelId: d.model,
      });

      legends.push({ label: constants.modelMap[d.model].nm, color: constants.modelMap[d.model].color });
    });

    return { graphs: processed, legends };
  }, [ ]);

  const handleFetchData = useCallback(async (filters, constants) => {
    const response = await getStatsData(DATE_RANGES[filters.range], MODULE_TYPES.BATTERY_TEMPERATURE);
    const { charging, discharging } = response[MODULE_TYPES.BATTERY_TEMPERATURE];
    const enums = STATS_ENUMS[MODULE_TYPES.BATTERY_TEMPERATURE];

    const categories = { charging: {}, discharging: {} };
    categories.charging = charging.map((category) => enums[category.idx]);
    categories.discharging = discharging.map((category) => enums[category.idx]);

    const defaultSelected = categories.charging[0];

    const dataMap = { charging: {}, discharging: {} };       // Map of graphData against category
    charging.forEach((d) => {
      dataMap.charging[d.idx] = processData(d.data, constants);
    });
    discharging.forEach((d) => {
      dataMap.discharging[d.idx] = processData(d.data, constants);
    });

    setSelected((old) => old || defaultSelected);
    setCategories((old) => old || categories);
    setPageData(dataMap);
    setIsLoading(false);
  }, [ processData ]);

  useEffect(() => {
    if (reportRef.current) reportRef.current.apply();
  }, [ handleFetchData ]);

  const handlePanelSwitch = (category) => {
    setSelected(category);
  };

  const getFilteredData = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  const handleClick = (row, col) => {
    const newPaths = [ ...paths ];
    newPaths[paths.length - 1] = {
      ...newPaths[paths.length - 1],
      link: PATHS.ANALYTICS + "/" + PATHS.BATTERY_TEMPERATURE,
      isCurrentPage: false,
    };
    newPaths.push({ idx: 2, name: selected.label, isCurrentPage: true });

    const graphData = getFilteredData(getSelectedData(pageData)[selected.idx].graphs);

    const state = {
      model: graphData[col].modelId,
      fault: MODULE_TYPES.BATTERY_TEMPERATURE,
      title: `Battery Temperature - ${selected.label}`,
      paths: newPaths,
    };

    navigate(PATHS.REPORTS, { state });
  };

  useEffect(() => {
    getVehicleStats(DATE_RANGES[filters.range], MODULE_TYPES.BATTERY_TEMPERATURE, (failure, success) => {
      if(failure) console.log(failure);
      else setBatteryTempData(getUserAndVehicle(success));
    })
  }, [])

  const removePopover = (toggle) => {
    if (toggle) setShowOverlay(!showOverlay);
    else setShowOverlay(false);
  };

  const columns = React.useMemo(() => Object.values(THRESHOLD_TABLE_HEADERS).map(header => {
    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => (header.popoverCard && row.value && row.value.length) ? (
        <PopoverCard
          data={row.value.length}
          cardTitle="Dates of Threshold crossed"
          array={getDateTimeRows(row.value)}
          removePopover={removePopover}
          dateTime={true}
        />
      ) : row.value || "-",
    };
  }), []);

  const getSelectedData = (data) => {
    const key = showChargingData ? "charging" : "discharging";
    return data[key];
  };

  const downloadData = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((vehicle, idx) => {
      vehicle["Sr No"] = idx + 1;
      vehicle["Vehicle ID"] = vehicle.idx;
      vehicle["Device ID"] = vehicle.did;
      vehicle["Vehicle Make"] = vehicle.vmk;
      vehicle["Vehicle Model"] = vehicle.mo;
      vehicle["City"] = vehicle.user.cName;
      vehicle["State"] = vehicle.user.sName;
      vehicle["User ID"] = vehicle.uid;
      vehicle["User Name"] = `${vehicle.user.fn} ${vehicle.user.ln}`;
      vehicle["User Mob"] = vehicle.user.mob;
      vehicle["User Email"] = vehicle.user.eml;
      vehicle["Times of Threshold Crossed"] = vehicle.stat.data;
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Vehicle ID, Device ID, Vehicle Make, Vehicle Model, City, State, Times of Threshold Crossed, User ID, User Name, User Mob, User Email";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Battery_Temprature_" + Date.now(), false);
  }

  return (
    <Report breadcrumb={paths} title={"Battery Temperature"} isLoading={isLoading} onApply={handleFetchData} ref={reportRef}>
      {!isLoading && (
        <>
          <div className="panel-chart-switch">
            <Switcher options={getSelectedData(categories)} selectedOption={selected.idx} handleChange={handlePanelSwitch} className="panel-graph-switcher panel-graph-switcher-xl" />
            <Checkbox className="widget-checkbox" isChecked={showChargingData} onChange={handleChange}>
              While Charging
            </Checkbox>
          </div>

          <div className="panel-chart-header">
            <ChartLegend data={getSelectedData(pageData)[selected.idx].legends} />
            <ModelPicker />
          </div>

          <div className="panel-chart-main">
            <label className="panel-chart-label">
              Times of Threshold Crossed, All Vehicles
            </label>

            <Stack data={getFilteredData(getSelectedData(pageData)[selected.idx].graphs)} labels={labelData} parser={parser} title="Threshold Crossed" onClick={handleClick} />
          </div>

          <div className="panel-chart-sheet">
            <div className="panel-chart-sheet-header">
              <IconButton
                className="panel-button"
                aria-label='Download Report'
                icon={<DownloadIcon />}
                onClick={() => downloadData(batteryTempData)}
              />
            </div>
            <div className="panel-chart-table">
              <ReactTable
                columns={columns}
                data={batteryTempData}
                showSearch={true}
                showOverlay={showOverlay}
              />
            </div>
          </div>
        </>
      )}
    </Report>
  );
}

export default BatteryTemperature;
